import { USE_BACKEND } from '../config/env';
import type { DataSource, Folder } from '../types';
import { getUserOverviewWithFallback } from './userOverviewService';
import { ROOT_FOLDER_ID } from '../constants';
import { setRootFolderUUID } from './dataObjectService.api';

// Fixed root folder ID - items assigned to this ID are considered root-level
// const ROOT_FOLDER_UUID = '05c5b1fe-817d-47d6-9fc7-abd6c814a0f0'; // Now imported from constants

export interface DataAdmissionBootstrapResult {
    folders: Folder[];
    dataSources: DataSource[];
    rootFolderUUID: string | null;
    isFirstLogin: boolean;
}

function uuidToNumeric(id: string): number {
    // If it's not a UUID format (like "root"), use a hash of the string
    if (!id.includes('-') || id.length < 36) {
        // Simple hash for non-UUID strings like "root"
        let hash = 0;
        for (let i = 0; i < id.length; i++) {
            const char = id.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash);
    }
    // Original UUID parsing logic for real UUIDs
    return parseInt(id.replace(/-/g, '').slice(-10), 16);
}

function legacyStatus(status: string): DataSource['status'] {
    switch (status) {
        case 'uploaded':
        case 'analyzing': return 'processing';
        case 'ready': return 'processed';
        case 'failed': return 'error';
        default: return 'processed';
    }
}

/**
 * Bootstrap user overview data (folders + data objects) using email-based authentication
 * Uses the new email-based user overview endpoint, with fallback to legacy separate calls
 */
export async function fetchDataAdmissionBootstrap(userEmail: string): Promise<DataAdmissionBootstrapResult> {
    if (!USE_BACKEND) {
        throw new Error('Bootstrap API only available when backend is enabled');
    }
    try {
        // Use the unified user overview endpoint with higher limits to get ALL data
        const data = await getUserOverviewWithFallback(userEmail, {
            include_empty_folders: true,
            limit_folders: 1000,
            limit_data_objects: 1000,
            include_inactive: true,
            include_deleted: true,
            include_all_statuses: true,
            status_filter: 'all'
        });

        // Detect the real root folder UUID from the backend response
        const rootFolder = data.folders.find(f => f.name === '__root__');
        // Only use the root folder ID if it's a real UUID (not the string 'root')
        let rootFolderUUID = (rootFolder?.id && rootFolder.id.includes('-') && rootFolder.id.length >= 36)
            ? rootFolder.id
            : null;

        // Build the set of visible (non-root, non-system) folder UUIDs
        const visibleFolderUuids = new Set(
            data.folders
                .filter(f => f.name !== '__root__' && f.id !== ROOT_FOLDER_ID)
                .map(f => f.id)
        );

        // If __root__ folder didn't have a valid UUID, infer root from data objects.
        // Any data object referencing a folder NOT in the visible set is root-level.
        if (!rootFolderUUID) {
            for (const o of data.data_objects) {
                if (o.folder_id && !visibleFolderUuids.has(o.folder_id) && o.folder_id !== ROOT_FOLDER_ID) {
                    rootFolderUUID = o.folder_id;
                    break;
                }
            }
        }

        // Cache root folder UUID so adaptBackendDataObject can identify root-level items
        setRootFolderUUID(rootFolderUUID);

        // Convert from new backend format to legacy frontend format
        const folders: Folder[] = data.folders
            .filter(f => f.name !== '__root__' && f.id !== ROOT_FOLDER_ID) // Hide root folder from UI
            .map(f => ({
                id: uuidToNumeric(f.id),
                uuid: f.id, // Store original UUID for API calls
                name: f.name,
                createdAt: f.created_at,
                dataObjectCount: f.data_object_count
            }));

        const dataSources: DataSource[] = data.data_objects.map(o => {
            const originalFileName = o.original_filename;
            const ext = originalFileName.split('.').pop()?.toLowerCase();
            const type = (() => {
                if (!ext) return 'csv';
                if (ext === 'xlsx' || ext === 'xls') return 'xlsx';
                if (ext === 'delta') return 'databricks';
                if (ext === 'csv') return 'csv';
                return 'csv'; // fallback
            })();
            // Derive short display name - prefer backend name field, fall back to original filename
            const shortName = (o as any).name || originalFileName.replace(/\.(delta|csv|xlsx|xls|json)$/i, '').split('.').slice(-1)[0];
            return {
                id: uuidToNumeric(o.id),
                uuid: o.id,
                name: shortName,
                originalFileName,
                type,
                size: 'Unknown',
                recordCount: 0,
                uploadedAt: o.created_at,
                status: legacyStatus(o.status),
                // Root-level if: no folder_id, folder_id matches root, or folder_id not in any visible folder
                folderId: (o.folder_id && visibleFolderUuids.has(o.folder_id))
                    ? uuidToNumeric(o.folder_id)
                    : undefined
            } as DataSource;
        });

        return { folders, dataSources, rootFolderUUID, isFirstLogin: data.is_first_login === true };
    } catch (err: any) {
        // No fallback - unified endpoint is required for container app
        throw err;
    }
}
